import _ from 'underscore';
import type {
  EdmCollectionType,
  ODataEntityResource,
  ODataOrderBy,
  ODataSearch,
  ODataSearchFilter,
  ParsedODataSearch,
} from '../types';

// support a mongoDB like orderby object for now: { age : -1, name: 1 }
// for backward compatibility, support array format: [['age', 'desc'], ['name', 'asc']]
function parseOrderBy(orderby?: ODataOrderBy): Pick<ParsedODataSearch, '$orderby'> {
  function lowerCaseOrderValue(value: [string, string]) {
    return _.map(value, (v, index) => (index === 1 ? v.toLowerCase() : v));
  }

  if (_.isEmpty(orderby)) {
    return {};
  }

  let $orderby;
  if (_.isArray(orderby)) {
    $orderby = _.map(
      orderby,
      value => lowerCaseOrderValue(value).join(' '),
    ).join(',');
  } else {
    $orderby = _.map(
      orderby,
      (value, key) => `${key} ${value > 0 ? 'asc' : 'desc'}`,
    ).join(',');
  }

  return {
    $orderby,
  };
}

function parseFilter(filter?: ODataSearchFilter): Pick<ParsedODataSearch, '$filter'> {
  if (_.isEmpty(filter) || _.isEmpty(filter?.where)) {
    return {};
  }

  return {
    $filter: filter?.where,
  };
}

function parseTop(top?: number): Pick<ParsedODataSearch, '$top'> {
  if (!top) {
    return {};
  }

  return {
    $top: top,
  };
}

function parseSkip(skip?: number): Pick<ParsedODataSearch, '$skip'> {
  if (!skip) {
    return {};
  }

  return {
    $skip: skip,
  };
}

function parseCount(count?: boolean): Pick<ParsedODataSearch, '$count'> {
  if (count === undefined) {
    return {};
  }

  return {
    $count: count,
  };
}

function parseSelect(
  select: string[] | undefined,
  resource: ODataEntityResource,
): Pick<ParsedODataSearch, '$select' | '$expand'> {
  if (!select) return {};
  const parsed: Pick<ParsedODataSearch, '$select' | '$expand'> = {};
  const { elementType } = resource.type as Partial<EdmCollectionType>;
  const selectedType = elementType || resource.type;
  const navigationPropertyNames = 'navigationPropertyNames' in selectedType
    ? selectedType.navigationPropertyNames
    : [];
  const $select = select
    .filter(item => !_.contains(navigationPropertyNames, item))
    .join(',');
  const $expand = select
    .filter(item => _.contains(navigationPropertyNames, item))
    .join(',');

  if ($select.length) {
    parsed.$select = $select;
  }

  if ($expand.length) {
    parsed.$expand = $expand;
  }

  return parsed;
}

function parseSearchString(search?: string): Pick<ParsedODataSearch, '$search'> {
  if (!search) {
    return {};
  }

  return {
    $search: search,
  };
}

export function parseSearch(
  search: ODataSearch,
  edmResource: ODataEntityResource,
): ParsedODataSearch {
  const {
    orderby,
    filter,
    top,
    skip,
    count,
    select,
    search: searchString,
    ...nonODataSearch
  } = search;

  return {
    ...parseOrderBy(orderby),
    ...parseFilter(filter),
    ...parseTop(top),
    ...parseSkip(skip),
    ...parseCount(count),
    ...parseSelect(select, edmResource),
    ...parseSearchString(searchString),
    ...nonODataSearch,
  };
}
